import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { LoginInput, MeDTO, Permission } from '@lightbm/shared';
import { setLanguage } from '@/i18n';
import { isApiError, onSessionEnded } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import { authApi } from './api';

interface AuthState {
  user: MeDTO | null;
  isLoading: boolean;
  /** The last session ended on its own (timeout, deactivation): explain it on the sign-in page. */
  sessionEnded: boolean;
  can: (permission: Permission) => boolean;
  canAny: (...permissions: Permission[]) => boolean;
  signIn: (input: LoginInput) => Promise<MeDTO>;
  signOut: () => Promise<void>;
  setUser: (user: MeDTO) => void;
}

const AuthContext = createContext<AuthState | null>(null);

async function loadMe(): Promise<MeDTO | null> {
  try {
    return await authApi.me();
  } catch (error) {
    if (isApiError(error) && error.status === 401) return null;
    throw error;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [sessionEnded, setSessionEnded] = useState(false);
  const meQuery = useQuery({
    queryKey: queryKeys.me,
    queryFn: loadMe,
    staleTime: Infinity,
    retry: false,
  });
  const user = meQuery.data ?? null;

  // Any API call answering 401 means the session is over: forget everything private.
  useEffect(
    () =>
      onSessionEnded(() => {
        setSessionEnded(true);
        queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'app' });
        queryClient.setQueryData(queryKeys.me, null);
      }),
    [queryClient],
  );

  // The interface follows the language chosen in the user's profile.
  useEffect(() => {
    if (user?.language) void setLanguage(user.language);
  }, [user?.language]);

  const signIn = useCallback(
    async (input: LoginInput) => {
      const me = await authApi.login(input);
      setSessionEnded(false);
      queryClient.setQueryData(queryKeys.me, me);
      return me;
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    await authApi.logout().catch(() => undefined);
    setSessionEnded(false);
    queryClient.clear();
    queryClient.setQueryData(queryKeys.me, null);
  }, [queryClient]);

  const setUser = useCallback(
    (me: MeDTO) => queryClient.setQueryData(queryKeys.me, me),
    [queryClient],
  );

  const value = useMemo<AuthState>(() => {
    const permissions = new Set(user?.permissions ?? []);
    return {
      user,
      isLoading: meQuery.isPending,
      sessionEnded,
      can: (permission) => permissions.has(permission),
      canAny: (...list) => list.some((permission) => permissions.has(permission)),
      signIn,
      signOut,
      setUser,
    };
  }, [user, meQuery.isPending, sessionEnded, signIn, signOut, setUser]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}

/** The signed-in user, inside routes protected by RequireAuth. */
export function useCurrentUser(): MeDTO {
  const { user } = useAuth();
  if (!user) throw new Error('useCurrentUser needs a signed-in user');
  return user;
}
